const sanitizeText = (value) => `${value || ""}`.trim();

const sanitizeIdPart = (value) =>
  sanitizeText(value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const defaultIdPrefix = "item";

export const createEditorId = (prefix = defaultIdPrefix) => {
  const safePrefix = sanitizeIdPart(prefix) || defaultIdPrefix;
  const randomSuffix = Math.random().toString(36).slice(2, 9);
  return `${safePrefix}-${Date.now().toString(36)}-${randomSuffix}`;
};

export const normalizeLabPolls = (polls) => {
  if (!Array.isArray(polls)) {
    return [];
  }

  const seenIds = new Set();

  return polls
    .map((poll, index) => {
      const question = sanitizeText(poll?.question || poll?.prompt || poll);
      if (!question) {
        return null;
      }

      const candidateId =
        sanitizeIdPart(poll?.id) || sanitizeIdPart(question).slice(0, 42) || `poll-${index + 1}`;
      let id = candidateId;
      let suffix = 2;
      while (seenIds.has(id)) {
        id = `${candidateId}-${suffix}`;
        suffix += 1;
      }
      seenIds.add(id);

      return {
        id,
        question,
      };
    })
    .filter(Boolean);
};

export const normalizeActivityLinks = (links) => {
  if (!Array.isArray(links)) {
    return [];
  }

  return links
    .map((link) => {
      const label = sanitizeText(link?.label);
      const url = sanitizeText(link?.url || link?.href || link);
      if (!url) {
        return null;
      }

      return {
        label: label || url,
        url,
      };
    })
    .filter(Boolean);
};

export const normalizeActivitySteps = (steps) => {
  if (!Array.isArray(steps)) {
    return [];
  }

  const seenIds = new Set();

  return steps
    .map((step, index) => {
      const text = sanitizeText(step?.text || step?.instruction || step);
      const prompt = sanitizeText(step?.prompt || step?.aiPrompt);
      if (!text && !prompt) {
        return null;
      }

      const candidateId =
        sanitizeIdPart(step?.id) || sanitizeIdPart(text).slice(0, 42) || `step-${index + 1}`;
      let id = candidateId;
      let suffix = 2;
      while (seenIds.has(id)) {
        id = `${candidateId}-${suffix}`;
        suffix += 1;
      }
      seenIds.add(id);

      return {
        id,
        text,
        prompt,
      };
    })
    .filter(Boolean);
};

export const normalizeLabActivities = (activities) => {
  if (!Array.isArray(activities)) {
    return [];
  }

  const seenIds = new Set();

  return activities
    .map((activity, index) => {
      const title = sanitizeText(activity?.title || activity?.name);
      const links = normalizeActivityLinks(activity?.links);
      const steps = normalizeActivitySteps(activity?.steps);

      if (!title && links.length === 0 && steps.length === 0) {
        return null;
      }

      const candidateId =
        sanitizeIdPart(activity?.id) ||
        sanitizeIdPart(title).slice(0, 42) ||
        `activity-${index + 1}`;
      let id = candidateId;
      let suffix = 2;
      while (seenIds.has(id)) {
        id = `${candidateId}-${suffix}`;
        suffix += 1;
      }
      seenIds.add(id);

      return {
        id,
        title: title || `Activity ${index + 1}`,
        links,
        steps,
      };
    })
    .filter(Boolean);
};
